/* Copyright (c) 2026 Xperi. All Rights Reserved. */
/**
 * Retry policy types per UAN-155 HTTP Retry Policy Requirements.
 *
 * @see http-retry-policy-requirements.md §4. Retry Policies
 */
export const RETRY_POLICY = {
  /**
   * The operation is never retried.
   * For non-critical metadata used for decorative UI elements.
   * Client must gracefully continue without the response data.
   */
  NEVER: "NEVER",

  /**
   * The operation is retried if appropriate for the HTTP code.
   * Uses fixed intervals [5s, 10s, 20s] - fails in < 1 minute.
   * For user-initiated operations like screen navigation or scheduling.
   */
  INTERACTIVE: "INTERACTIVE",

  /**
   * The operation is retried until success - never fails.
   * Uses intervals [60s, 240s, 720s, 1000s, 2000s, 4000s], repeats last.
   * For critical operations like login, provisioning, and channel data.
   */
  CRITICAL: "CRITICAL"
} as const;

export type RetryPolicy = (typeof RETRY_POLICY)[keyof typeof RETRY_POLICY];
/**
 * Fixed retry intervals per policy (in milliseconds).
 *
 * @see http-retry-policy-requirements.md §9. Retry Backoff Client Policy
 */
export const RETRY_INTERVALS_MS = {
  /**
   * INTERACTIVE: [5s, 10s, 20s] = 35s total (< 1 minute).
   * 3 retry attempts, then fails.
   */
  INTERACTIVE: [5000, 10000, 20000] as Array<number>,

  /**
   * CRITICAL: [60s, 240s, 720s, 1000s, 2000s, 4000s].
   * Repeats last interval (4000s) until success - never fails.
   */
  CRITICAL: [60000, 240000, 720000, 1000000, 2000000, 4000000] as Array<number>
};
/**
 * Standard Retry-After header name.
 */
/**
 * HTTP methods considered idempotent (safe to auto-retry on 502/504).
 *
 * @see http-retry-policy-requirements.md §5.3 Idempotency
 */
export const IDEMPOTENT_HTTP_METHODS = [
  "GET",
  "HEAD",
  "OPTIONS",
  "PUT",
  "DELETE"
] as const;

export type HttpMethod =
  | "GET"
  | "HEAD"
  | "OPTIONS"
  | "PUT"
  | "DELETE"
  | "POST"
  | "PATCH"
  | "CONNECT";

/**
 * Configuration options for the RetryMechanism (policy-based only).
 *
 * @see http-retry-policy-requirements.md
 */
export interface TRetryOptions {
  /**
   * The retry policy to apply to this operation.
   * Determines retry behavior and backoff intervals.
   *
   * - NEVER: Never retry, gracefully continue without data.
   * - INTERACTIVE: Retry with [5s, 10s, 20s] intervals, fail < 1 minute.
   * - CRITICAL: Retry indefinitely with [60s...4000s] intervals.
   */
  retryPolicy?: RetryPolicy;

  /**
   * The HTTP method of the operation (for idempotency checking).
   * Required for proper 502/504 retry decisions.
   */
  httpMethod?: HttpMethod;

  /**
   * Optional callback invoked each time a retry attempt is about to begin.
   * Fires after the error is determined retryable and before the backoff
   * wait starts.
   *
   * @param error - The error that triggered the retry
   * @param attempt - Zero-based attempt number (0 = first failure)
   * @param delayMs - The backoff delay in milliseconds before the next try
   */
  onRetry?: (error: unknown, attempt: number, delayMs: number) => void;

  /**
   * Optional callback invoked when a retry attempt succeeds after at least
   * one previous failure. NOT called on first-try success.
   */
  onSuccess?: () => void;

  /**
   * Optional predicate declaring an error unfixable by retrying. When it
   * returns true the error is rethrown immediately, whatever the policy.
   *
   * Exists because `CRITICAL` deliberately "retries ALL errors regardless of
   * HTTP Error Code Specification retryability" (UAN-155 §2.3/§4.3) and never
   * fails. That is right for a connection that may come back, and wrong for a
   * definitive "no" — a 409 TOO_MANY_DEVICES or a 401 NOT_AUTHORIZED will
   * answer the same way forever, so the device retries indefinitely while the
   * caller shows a blocking overlay.
   *
   * Opt-in, so UAN-155 semantics are unchanged for every caller that does not
   * pass it. Sign-in supplies `isTerminalSignInError` (UAW-2416 AC-4 / D1).
   */
  isTerminal?: (error: unknown) => boolean;

  /**
   * Optional provider of a "retry now" signal, read fresh before each backoff
   * wait. When the returned signal aborts while the loop is sleeping between
   * attempts, the backoff ends early and the same call is re-attempted
   * immediately — unlike `abortSignal`, which cancels the loop entirely.
   *
   * Lets a manual Retry button collapse the CRITICAL interval without starting
   * a second request: the wake only shortens the existing loop's sleep, and is
   * a no-op while a request is already in flight. Opt-in — callers that omit it
   * keep the pure timer-driven backoff.
   */
  getRetryNowSignal?: () => AbortSignal | undefined;

  /**
   * Optional quiet warm-up delays (ms) applied before the policy ladder takes
   * over. Callers that surface a blocking overlay use this to retry silently a
   * few times first — e.g. [1000, 3000, 5000] — and only show the overlay once
   * the warm-up is exhausted. After the warm-up the policy ladder runs from its
   * start. Opt-in; omitting it keeps pure policy-driven backoff.
   */
  initialRetryDelaysMs?: readonly number[];
}
